import React, { useEffect, useState, FormEvent, useContext } from 'react';
import {
	ModalBase,
	Form,
	BaseStyles,
	Switch,
	notify,
	Thumbnail,
	Button,
} from '@bynder/design-system';
import { IconInfo } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { Errors, User } from './types';
import { API } from './api';
import TimeFrame from './Timeframe';
import { StayNotified } from './StayNotified';
import { Divider } from './styles';
import SelectUser from './SelectUser';
import { track } from '../../helpers/track';
import { InfoContext } from '../../context';
import { useCustomEvent } from '../../helpers/useCustomEvent';

export function OutOfOfficeModal() {
	const [isOpen, setIsOpen] = useState(false);
	const [id, setId] = useState('');
	const [selectedUser, setSelectedUser] = useState<User>();
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [stayNotified, setStayNotified] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorName, setErrorName] = useState<Errors>();
	const { translate } = useTranslate();
	const { setLastChange } = useContext(InfoContext);

	const errorTimeFrame: Errors[] = ['user_has_overlaping_ooo', 'main_already_backup'];
	const errorSelectUser: Errors[] = ['backup_unavailable'];

	const allFieldsSet = selectedUser && !!startDate && !!endDate;
	const allFieldsEmpty = !selectedUser && startDate === '' && endDate === '';

	const isNewOOO = id === '';
	const editAndAllEmpty = !isNewOOO && allFieldsEmpty;
	const editAndAllSet = !isNewOOO && allFieldsSet;
	const newAndAllSet = isNewOOO && allFieldsSet;

	const requiredFormState = newAndAllSet || editAndAllSet || editAndAllEmpty;

	useCustomEvent('openOutOfOfficeModal', openModal);

	function openModal() {
		setIsOpen(true);
	}

	function closeModal() {
		setIsOpen(false);
	}

	const getTrackingEventName = () => {
		if (isNewOOO) {
			return 'Out of office Created';
		}

		if (!isNewOOO && allFieldsSet) {
			return 'Out of office Changed';
		}

		if (!isNewOOO && allFieldsEmpty) {
			return 'Out of office Removed';
		}
	};

	const onSave = async () => {
		const eventName = getTrackingEventName();

		if (eventName) {
			track(eventName);
		}

		setIsLoading(true);

		try {
			if (id) {
				await API.update({
					id,
					from_date: startDate,
					to_date: endDate,
					backup_user_id: selectedUser?.id || '',
					main_user_notifications: stayNotified,
				});
			} else {
				await API.save({
					from_date: startDate,
					to_date: endDate,
					backup_user_id: selectedUser?.id || '',
					main_user_notifications: stayNotified,
				});
			}

			notify({
				variant: 'success',
				title: translate('user-outofoffice.SetOutOfOfficeSuccess'),
			});

			setLastChange();

			setIsLoading(false);
			closeModal();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			setErrorName(err.error);
			setIsLoading(false);
		}
	};

	const onRemoveUser = () => {
		setSelectedUser(undefined);
	};

	const onClearOOO = async () => {
		setIsLoading(true);

		try {
			await API.remove(id);
			notify({
				variant: 'success',
				title: translate('user-outofoffice.SetOutOfOfficeClearSuccess'),
			});

			setLastChange();
		} catch (err) {
			notify({
				thumbnail: (
					<Thumbnail
						variant="clean"
						shape="circle"
						backgroundColor="yellow500"
						icon={<IconInfo />}
						iconColor="white"
					/>
				),

				title: translate('user-outofoffice.SetOutOfOfficeClearError'),
			});
		}

		reset();
		setIsLoading(false);
		closeModal();
	};

	function reset() {
		setStartDate('');
		setEndDate('');
		setStayNotified(false);
		setId('');
		setSelectedUser(undefined);
	}

	const onStayNotifiedChange = (event: FormEvent<HTMLInputElement>) => {
		setStayNotified(event.currentTarget.checked);
	};

	useEffect(() => {
		async function checkAvailabilitySet() {
			try {
				const response = await API.check();
				const ooo = response[0];

				if (ooo) {
					const name = `${ooo.backup_user_data.firstName} ${
						ooo.backup_user_data.infix ?? ''
					} ${ooo.backup_user_data.lastName}`;

					setSelectedUser({
						id: ooo.backup_user_data.id,
						name,
						email: ooo.backup_user_data.email,
					});
					setStartDate(ooo.from_date || '');
					setEndDate(ooo.to_date);
					setStayNotified(ooo.main_user_notifications);
					setId(ooo.id);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setIsLoading(false);
			}
		}

		if (isOpen) {
			setIsLoading(true);
			checkAvailabilitySet();
			track('Out of office Opened');
		}
	}, [isOpen]);

	useEffect(() => {
		setErrorName(undefined);
	}, [selectedUser, startDate, endDate]);

	return (
		<ModalBase isOpen={isOpen} onClose={closeModal}>
			<ModalBase.Header title={translate('user-outofoffice.SetYourselfAsOutOfOffice')} />
			<ModalBase.Content>
				<BaseStyles>
					<Form>
						<Form.Row>
							<TimeFrame
								startDate={startDate}
								endDate={endDate}
								onStartDateChange={(value) => {
									setStartDate(value || '');
								}}
								onEndDateChange={setEndDate}
							/>
						</Form.Row>
						{errorTimeFrame.find((error) => error === errorName) && (
							<Form.Row>
								<Form.Group>
									<Form.HelperText icon={<Form.IconError />} isInvalid>
										{errorName === 'main_already_backup'
											? translate('user-outofoffice.MainAlreadyBackup')
											: translate('user-outofoffice.UserHasOverlapingOOO')}
									</Form.HelperText>
								</Form.Group>
							</Form.Row>
						)}
						<Form.Group>
							<Form.Label>{translate('user-outofoffice.BackupUser')}</Form.Label>
							<SelectUser
								onSelectUser={setSelectedUser}
								selectedUser={selectedUser}
								onRemoveUser={onRemoveUser}
								errorName={errorSelectUser.find((error) => error === errorName)}
							/>
						</Form.Group>
						<Divider />
						<StayNotified>
							<Switch
								onChange={onStayNotifiedChange}
								isChecked={stayNotified}
								isDisabled={isLoading}
								aria-label={translate(
									'user-outofoffice.KeepReceivingNotifications',
								)}
							/>
						</StayNotified>
					</Form>
				</BaseStyles>
			</ModalBase.Content>
			<ModalBase.Footer
				actionTertiary={
					!isNewOOO && (
						<Button onClick={onClearOOO} variant="secondary">
							{translate('user-outofoffice.SetOutOfOfficeClear')}
						</Button>
					)
				}
				actionSecondary={
					<Button onClick={closeModal} variant="secondary">
						{translate('generalCancel')}
					</Button>
				}
				actionPrimary={
					<Button
						onClick={onSave}
						variant="primary"
						isDisabled={isLoading || !requiredFormState}
					>
						{translate('save')}
					</Button>
				}
			/>
		</ModalBase>
	);
}
